<template>
  <v-btn
    icon
    variant="text"
    class="activity-bell"
    @click="togglePanel"
  >
    <v-badge
      v-if="unreadCount > 0"
      :content="unreadCount > 99 ? '99+' : unreadCount"
      color="error"
      floating
    >
      <v-icon>mdi-bell</v-icon>
    </v-badge>
    <v-icon v-else>
      mdi-bell-outline
    </v-icon>

    <v-tooltip
      activator="parent"
      location="bottom"
    >
      {{ unreadCount > 0 ? `${unreadCount} непрочитанных` : 'Лента событий' }}
    </v-tooltip>
  </v-btn>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useActivityFeedStore } from '@/stores/activityFeed'
import { useAuthStore } from '@/stores/auth'
import { useNotifications, type ActivityFeedEvent } from '@/composables/useNotifications'
import { peopleApi } from '@/plugins/api'

const activityFeedStore = useActivityFeedStore()
const authStore = useAuthStore()
const { unreadCount } = storeToRefs(activityFeedStore)
const { togglePanel } = activityFeedStore

const { subscribeToActivity, isConnected } = useNotifications()

let unsubscribe: (() => void) | null = null
const cachedPersonId = ref<string | null>(null)
const isSubscribed = ref(false)

// Get personId from API (cached)
async function getPersonId(): Promise<string | null> {
  if (cachedPersonId.value) return cachedPersonId.value

  try {
    const response = await peopleApi.getMyProfile()
    cachedPersonId.value = (response.data as { id: string })?.id || null
    return cachedPersonId.value
  } catch {
    return null
  }
}

// Subscribe to activity updates when user is authenticated
async function setupActivitySubscription() {
  // Avoid duplicate subscriptions
  if (isSubscribed.value) return
  if (!authStore.isAuthenticated) return
  if (!isConnected.value) {
    console.debug('[ActivityFeedBell] WebSocket not connected yet, waiting...')
    return
  }

  const personId = await getPersonId()
  if (!personId) {
    console.debug('[ActivityFeedBell] No personId, cannot subscribe')
    return
  }

  // Cleanup previous subscription if any
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }

  console.debug('[ActivityFeedBell] Subscribing to activity for user:', personId)

  unsubscribe = subscribeToActivity(
    personId,
    (event: ActivityFeedEvent) => {
      console.debug('[ActivityFeedBell] Received activity:new event:', event)
      activityFeedStore.handleNewActivity(event.activity as Parameters<typeof activityFeedStore.handleNewActivity>[0])
    },
    (count: number) => {
      console.debug('[ActivityFeedBell] Received activity:unread-count:', count)
      activityFeedStore.handleUnreadCountUpdate(count)
    },
    (readIds: string[]) => {
      console.debug('[ActivityFeedBell] Received activity:read-sync:', readIds)
      activityFeedStore.handleReadStatusSync(readIds)
    }
  )

  isSubscribed.value = true
}

// Computed to track when we should try to subscribe
const canSubscribe = computed(() => authStore.isAuthenticated && isConnected.value)

onMounted(() => {
  activityFeedStore.init()
  // Try to subscribe if already ready
  if (canSubscribe.value) {
    setupActivitySubscription()
  }
})

// Re-subscribe when connection or auth status changes
watch(canSubscribe, (ready) => {
  if (ready && !isSubscribed.value) {
    setupActivitySubscription()
  }
}, { immediate: true })

// Also watch connection specifically for reconnects
watch(isConnected, (connected) => {
  if (connected && authStore.isAuthenticated) {
    // Reset subscription state on reconnect to re-subscribe
    isSubscribed.value = false
    setupActivitySubscription()
  }
})

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
  }
})
</script>

<style scoped>
.activity-bell {
  transition: transform 0.2s ease;
}

.activity-bell:hover {
  transform: scale(1.1);
}
</style>
